import React, { useState, createContext, useContext, useCallback } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { useLanguage } from '../context/LanguageContext';
import { AlertTriangle, Trash2, Info, HelpCircle } from 'lucide-react';

// Context pour gérer les dialogues de confirmation
const ConfirmContext = createContext(null);

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
};

export const ConfirmProvider = ({ children }) => {
  const [dialog, setDialog] = useState({
    open: false,
    title: '',
    message: '',
    type: 'warning', // 'warning', 'danger', 'info'
    confirmText: '',
    cancelText: '',
    onConfirm: () => {},
    onCancel: () => {},
  });

  const confirm = useCallback(({ title, message, type = 'warning', confirmText, cancelText }) => {
    return new Promise((resolve) => {
      setDialog({
        open: true,
        title,
        message,
        type,
        confirmText,
        cancelText,
        onConfirm: () => {
          setDialog(prev => ({ ...prev, open: false }));
          resolve(true);
        },
        onCancel: () => {
          setDialog(prev => ({ ...prev, open: false }));
          resolve(false);
        },
      });
    });
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialogUI {...dialog} />
    </ConfirmContext.Provider>
  );
};

// Composant UI du dialogue
const ConfirmDialogUI = ({ 
  open, 
  title, 
  message, 
  type, 
  confirmText, 
  cancelText, 
  onConfirm, 
  onCancel 
}) => {
  const { t } = useLanguage();

  const getIcon = () => {
    switch (type) {
      case 'danger':
        return <Trash2 className="w-6 h-6 text-red-500" />;
      case 'info':
        return <Info className="w-6 h-6 text-blue-500" />;
      case 'warning':
      default:
        return <AlertTriangle className="w-6 h-6 text-amber-500" />;
    }
  };

  const getConfirmButtonClass = () => {
    switch (type) {
      case 'danger':
        return 'bg-red-600 hover:bg-red-700 text-white border-0';
      case 'info':
        return 'bg-blue-600 hover:bg-blue-700 text-white border-0';
      case 'warning':
      default:
        return 'bg-amber-600 hover:bg-amber-700 text-white border-0';
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <AlertDialogContent className="bg-gradient-to-br from-zinc-900 to-zinc-950 border border-zinc-700/50 shadow-2xl max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-full bg-zinc-800/80">
              {getIcon()}
            </div>
            <AlertDialogTitle className="text-lg font-semibold text-zinc-100">
              {title || t('confirm')}
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-zinc-400 text-sm leading-relaxed pl-12">
            {message}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="mt-4 gap-2 sm:gap-2">
          <AlertDialogCancel 
            onClick={onCancel}
            className="bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border-zinc-600 transition-colors"
          >
            {cancelText || t('cancel')}
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={onConfirm}
            className={`${getConfirmButtonClass()} transition-colors`}
          >
            {confirmText || t('confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmDialogUI;
